/**
 * Visual effects helper. All effects are procedural (Graphics + tweens),
 * no external assets. Each function spawns short-lived objects that clean
 * themselves up via tween onComplete callbacks.
 *
 * Conventions:
 *   - Every effect takes the Scene as the first arg.
 *   - Effects scale visually with the scene's current speed where it matters
 *     (callers can pass durations themselves; default durations are 1x).
 */
import Phaser from "phaser";
import { THEME } from "../ui/theme";
import { fx } from "../systems/FxPool";

/** Impact sparks at a point. Pooled — safe to call on every hit. */
export function impactSparks(x: number, y: number, count = 3): void {
  fx.sparks(x, y, count);
}

/** Floating damage number that drifts up and fades. Pooled. */
export function damageNumber(
  _scene: Phaser.Scene, x: number, y: number, amount: number, color = "#ffe070", crit = false
): void {
  fx.damageNumber(x, y, amount, color, crit);
}

/** Gold reward popup at enemy death position. Pooled. */
export function goldPopup(_scene: Phaser.Scene, x: number, y: number, amount: number): void {
  fx.goldPopup(x, y, amount);
}

/** Burst of colored pixel-particles from a death point. Pooled. */
export function deathBurst(_scene: Phaser.Scene, x: number, y: number, color: number, count = 8): void {
  fx.burst(x, y, color, count);
  fx.decal(x, y + 4);
}

/**
 * Boss death shockwave: a hot core, two expanding rings offset in time, and a
 * ring of debris thrown outward. Built from pooled objects so even a triple
 * boss wave can't spike allocation.
 */
export function bossDeathShockwave(scene: Phaser.Scene, x: number, y: number): void {
  // Rings reuse the frost splash texture as a generic soft ring when present.
  const ringTex = scene.textures.exists("vfx-frost-splash") ? "vfx-frost-splash" : null;
  if (ringTex) {
    fx.sprite(ringTex, x, y, {
      w: 300, h: 300, lifeMs: 620, depth: 880, alphaFrom: 0.75,
      startScale: 0.12, growTo: 1, additive: true
    });
    fx.sprite(ringTex, x, y, {
      w: 460, h: 460, lifeMs: 900, depth: 875, alphaFrom: 0.4,
      startScale: 0.08, growTo: 1, additive: true
    });
  }
  if (scene.textures.exists("vfx-cannon-explosion")) {
    fx.sprite("vfx-cannon-explosion", x, y, {
      lifeMs: 520, depth: 885, alphaFrom: 1, startScale: 0.3, growTo: 1.9, additive: true
    });
  }
  // Debris ring — wider and heavier than a normal death burst.
  fx.burst(x, y, 0xffd070, 18, 150);
  fx.burst(x, y, 0xff8844, 12, 96);
  for (let i = 0; i < 6; i++) fx.puff(x + (Math.random() - 0.5) * 60, y + (Math.random() - 0.5) * 30, 0x4a3a30, 9, 0.5);
  fx.decal(x, y + 4, 46, 16, 0x1a0a04, 0.55);
}

/** Brief muzzle flash at a tower's firing point. */
export function muzzleFlash(
  scene: Phaser.Scene,
  x: number,
  y: number,
  towerKey = "archer",
  color = 0xffe070,
  rotation = 0
): void {
  const textureKey = `vfx-${towerKey}-muzzle`;
  if (scene.textures.exists(textureKey)) {
    fx.sprite(textureKey, x, y, {
      lifeMs: towerKey === "cannon" ? 150 : 110,
      rotation,
      depth: 800,
      alphaFrom: 0.95,
      startScale: 0.65,
      growTo: 1.15,
      additive: true
    });
    return;
  }
  const flash = scene.add.circle(x, y, 6, color, 0.9).setDepth(800);
  flash.setBlendMode(Phaser.BlendModes.ADD);
  scene.tweens.add({
    targets: flash,
    scale: 2,
    alpha: 0,
    duration: 120,
    ease: "Quad.Out",
    onComplete: () => flash.destroy()
  });
}

/** Cannon-style explosion: bright flash, expanding shockwave, smoke. */
export function explosion(scene: Phaser.Scene, x: number, y: number, radius: number): void {
  if (scene.textures.exists("vfx-cannon-explosion")) {
    fx.sprite("vfx-cannon-explosion", x, y, {
      lifeMs: 430, depth: 850, alphaFrom: 0.98, startScale: 0.35, growTo: 1, additive: true
    });
    return;
  }
  // Inner flash
  const core = scene.add.circle(x, y, radius * 0.4, 0xffe070, 1).setDepth(850);
  scene.tweens.add({
    targets: core,
    scale: 1.6, alpha: 0, duration: 220, ease: "Cubic.Out",
    onComplete: () => core.destroy()
  });
  // Shockwave ring
  const ring = scene.add.circle(x, y, 4, 0xff8030, 0).setStrokeStyle(3, 0xff8030, 1).setDepth(840);
  scene.tweens.add({
    targets: ring,
    radius, alpha: 0, duration: 350, ease: "Cubic.Out",
    onComplete: () => ring.destroy()
  });
  // Smoke puff
  for (let i = 0; i < 5; i++) {
    const angle = Math.random() * Math.PI * 2;
    const r = Math.random() * radius * 0.5;
    const smoke = scene.add.circle(x + Math.cos(angle) * r, y + Math.sin(angle) * r, 4 + Math.random() * 3, 0x4a3a30, 0.6).setDepth(820);
    scene.tweens.add({
      targets: smoke,
      y: smoke.y - 10 - Math.random() * 8,
      alpha: 0,
      scale: 1.8,
      duration: 600,
      onComplete: () => smoke.destroy()
    });
  }
}

/** Pale-cyan ring expanding to a frost splash radius. */
export function frostBurst(scene: Phaser.Scene, x: number, y: number, radius: number): void {
  if (scene.textures.exists("vfx-frost-splash")) {
    fx.sprite("vfx-frost-splash", x, y, {
      lifeMs: 520, depth: 840, alphaFrom: 0.9, startScale: 0.4, growTo: 1, additive: true
    });
    return;
  }
  const ring = scene.add.circle(x, y, 4, 0x6cc7ff, 0).setStrokeStyle(2, 0xaee0ff, 1).setDepth(840);
  scene.tweens.add({ targets: ring, radius, alpha: 0, duration: 420, ease: "Cubic.Out", onComplete: () => ring.destroy() });
  // Snow flakes
  for (let i = 0; i < 6; i++) {
    const a = Math.random() * Math.PI * 2;
    const d = radius * (0.4 + Math.random() * 0.5);
    const flake = scene.add.rectangle(x, y, 2, 2, 0xffffff, 0.95).setDepth(835);
    scene.tweens.add({
      targets: flake,
      x: x + Math.cos(a) * d,
      y: y + Math.sin(a) * d,
      alpha: 0,
      duration: 500,
      onComplete: () => flake.destroy()
    });
  }
}

/** Jagged lightning polyline between two points, redrawn a few times then gone. */
export function chainLightning(scene: Phaser.Scene, x1: number, y1: number, x2: number, y2: number, color = 0xffffff): void {
  if (scene.textures.exists("vfx-lightning-bolt")) {
    const distance = Phaser.Math.Distance.Between(x1, y1, x2, y2);
    fx.sprite("vfx-lightning-bolt", (x1 + x2) / 2, (y1 + y2) / 2, {
      w: distance, h: 18, lifeMs: 170, depth: 900, alphaFrom: 0.95,
      rotation: Phaser.Math.Angle.Between(x1, y1, x2, y2),
      // Bolt keeps its length and thins out vertically as it fades.
      growToX: 1, growTo: 0.45, additive: true
    });
    return;
  }
  const g = scene.add.graphics().setDepth(900);
  const draw = () => {
    g.clear();
    g.lineStyle(2, color, 1);
    g.beginPath();
    g.moveTo(x1, y1);
    const segs = 6;
    for (let i = 1; i < segs; i++) {
      const t = i / segs;
      const px = x1 + (x2 - x1) * t + (Math.random() - 0.5) * 12;
      const py = y1 + (y2 - y1) * t + (Math.random() - 0.5) * 12;
      g.lineTo(px, py);
    }
    g.lineTo(x2, y2);
    g.strokePath();
    // Glow underline
    g.lineStyle(4, color, 0.3);
    g.strokePath();
  };
  draw();
  let frames = 0;
  const ev = scene.time.addEvent({
    delay: 40,
    repeat: 3,
    callback: () => {
      frames++;
      draw();
      if (frames >= 3) {
        scene.time.delayedCall(60, () => g.destroy());
        ev.remove(false);
      }
    }
  });
}

/** Lingering green poison cloud. */
export function poisonCloud(scene: Phaser.Scene, x: number, y: number, radius: number): void {
  if (scene.textures.exists("vfx-poison-cloud")) {
    fx.sprite("vfx-poison-cloud", x, y, {
      lifeMs: 1050, depth: 820, alphaFrom: 0.7, startScale: 0.55, growTo: 1.05,
      vy: -8 / 1.05   // the old tween drifted 8px up over its 1050ms lifetime
    });
    return;
  }
  for (let i = 0; i < 8; i++) {
    const a = Math.random() * Math.PI * 2;
    const d = Math.random() * radius;
    const bx = x + Math.cos(a) * d;
    const by = y + Math.sin(a) * d;
    const c = scene.add.circle(bx, by, 3 + Math.random() * 3, 0x6abf3a, 0.55).setDepth(820);
    scene.tweens.add({
      targets: c,
      y: by - 14 - Math.random() * 8,
      alpha: 0,
      scale: 1.6,
      duration: 900 + Math.random() * 300,
      onComplete: () => c.destroy()
    });
  }
}

/** Vertical light beam + ground ring for holy smite. */
export function holySmite(scene: Phaser.Scene, x: number, y: number): void {
  if (scene.textures.exists("vfx-holy-beam") && scene.textures.exists("vfx-holy-ground-ring")) {
    fx.sprite("vfx-holy-beam", x, y - 116, {
      w: 58, h: 236, lifeMs: 360, depth: 870, growToX: 1.35, growTo: 1, additive: true
    });
    fx.sprite("vfx-holy-ground-ring", x, y, {
      lifeMs: 430, depth: 860, startScale: 0.4, growTo: 1.2, additive: true
    });
    return;
  }
  const beam = scene.add.rectangle(x, y - 200, 16, 400, 0xfff2a8, 0.75).setOrigin(0.5, 0).setDepth(870);
  scene.tweens.add({
    targets: beam,
    alpha: 0,
    scaleX: 2,
    duration: 320,
    onComplete: () => beam.destroy()
  });
  const ring = scene.add.circle(x, y, 4, 0xfff2a8, 0).setStrokeStyle(2, 0xffe070, 1).setDepth(860);
  scene.tweens.add({ targets: ring, radius: 36, alpha: 0, duration: 360, onComplete: () => ring.destroy() });
}

/** Pierce trail behind a fast bolt. */
export function pierceTrail(scene: Phaser.Scene, x: number, y: number, rotation = 0, color = 0xffffff): void {
  if (scene.textures.exists("vfx-crossbow-trail")) {
    fx.sprite("vfx-crossbow-trail", x, y, {
      w: 34, h: 9, lifeMs: 180, depth: 700, alphaFrom: 0.7, rotation,
      growToX: 0.35, growTo: 1   // shortens along its length only
    });
    return;
  }
  const t = scene.add.rectangle(x, y, 4, 2, color, 0.8).setDepth(700);
  scene.tweens.add({ targets: t, alpha: 0, scale: 0.4, duration: 220, onComplete: () => t.destroy() });
}

/** Compact spark bloom at a Tesla coil or lightning impact point. */
export function lightningSparks(scene: Phaser.Scene, x: number, y: number, size = 34): void {
  if (scene.textures.exists("vfx-lightning-sparks")) {
    fx.sprite("vfx-lightning-sparks", x, y, {
      lifeMs: 230, depth: 905, alphaFrom: 0.95, startScale: 0.45, growTo: 1.15,
      // Random spawn tilt replaces the old angle tween — same visual variety.
      rotation: Phaser.Math.DegToRad(Phaser.Math.Between(-25, 25)),
      additive: true
    });
    return;
  }

  const burst = scene.add.circle(x, y, 3, 0xfff099, 0.95).setDepth(905);
  burst.setBlendMode(Phaser.BlendModes.ADD);
  scene.tweens.add({
    targets: burst,
    radius: size * 0.45,
    alpha: 0,
    duration: 180,
    ease: "Cubic.Out",
    onComplete: () => burst.destroy()
  });
}

export function magicImpact(_scene: Phaser.Scene, x: number, y: number): void {
  fx.sprite("vfx-mage-impact", x, y, {
    lifeMs: 330, depth: 850, startScale: 0.4, growTo: 1, additive: true
  });
}

export function arrowTrail(_scene: Phaser.Scene, x: number, y: number, rotation: number): void {
  fx.sprite("vfx-archer-trail", x, y, {
    w: 24, h: 8, lifeMs: 150, depth: 690, alphaFrom: 0.42, rotation,
    growToX: 0.4, growTo: 1
  });
}

/** Gold ring + level-up text when a tower upgrades. */
export function levelUpEffect(scene: Phaser.Scene, x: number, y: number, level: number): void {
  const ring = scene.add.circle(x, y + 8, 8, 0xffe070, 0).setStrokeStyle(3, 0xffe070, 1).setDepth(900);
  scene.tweens.add({ targets: ring, radius: 48, alpha: 0, duration: 600, ease: "Cubic.Out", onComplete: () => ring.destroy() });
  const t = scene.add.text(x, y - 12, `+ LV ${level}`, {
    fontFamily: THEME.fonts.body,
    fontSize: "18px",
    color: "#ffe070",
    stroke: "#000000",
    strokeThickness: 4
  }).setOrigin(0.5).setDepth(950);
  scene.tweens.add({ targets: t, y: y - 36, alpha: 0, duration: 900, ease: "Cubic.Out", onComplete: () => t.destroy() });
}

/** Persistent slow-tint shimmer for an enemy under a slow debuff. */
export function applySlowTint(target: Phaser.GameObjects.Image): void {
  target.setTint(0x88ccff);
}

export function clearDebuffTint(target: Phaser.GameObjects.Image): void {
  target.clearTint();
}
